import { useState, useRef } from "react";
import styled from "styled-components";
import { Tooltip } from "react-tooltip";
import { CopyToClipboard } from "react-copy-to-clipboard";

import { COUNTRIES, COUNTRY_ICONS } from "constants/countries";
import { dateFormatted, getTimeAMPM } from "modules/functions";
import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import ButtonRoundIcon from "components/buttons/ButtonRoundIcon";
import SectionTitle from "components/text/SectionTitle";
import RoundActionIcon from "components/buttons/RoundActionIcon";
import ContextMenu from "components/menu/ContextMenu";

import leftIcon from "assets/img/icons/left-arrow.svg";
import rightIcon from "assets/img/icons/right-arrow.svg";
import leftWhiteIcon from "assets/img/icons/left-arrow-white.svg";
import rightWhiteIcon from "assets/img/icons/right-arrow-white.svg";
import blockIcon from "assets/img/icons/icons8-unavailable.svg";
import mailPreviewIcon from "assets/img/icons/mail-preview-icon.svg";
import mailPreviewWhiteIcon from "assets/img/icons/mail-preview-icon-white.svg";
import circleGreyIcon from "assets/img/icons/icons8-circle-grey.svg";

const SentEmailsSection = styled.div``;

const Header = styled.div`
  margin-bottom: 12px;

  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 500;
`;

const LeadList = styled.div`
  width: 100%;
  padding: ${css.BOX_PADDING_PRIMARY};
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
`;

const TableWrap = styled.div`
  width: 100%;
  padding-bottom: 5px;
  margin-bottom: -5px;

  overflow-x: auto;
`;

const ListTable = styled.table`
  width: 100%;
  min-width: 520px;

  border-spacing: 0;
`;

const TableHead = styled.thead``;

const Label = styled.th`
  padding: 0 5px 12px 5px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  font-size: 17px;
  font-weight: 400;
  text-align: left;
`;

const TableBody = styled.tbody``;

const Cell = styled.td`
  padding: 8px 4px;
  max-width: ${(props) => (props.width ? props.width : "none")};

  white-space: nowrap;
  font-size: 15px;
`;

const CellVeryLeft = styled.div`
  padding-left: 18px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-left: 10px;
  }
`;

const CellVeryRight = styled.div`
  padding-right: 18px;

  @media (max-width: ${globalConstants.switchTablet}) {
    padding-right: 10px;
  }
`;

const CellLineValueWrap = styled.div`
  padding-right: 10px;

  display: flex;
  align-items: center;
  gap: 6px;
`;

const Pagination = styled.div`
  width: 100%;
  margin-top: ${css.VERTICAL_BOX_MARGIN};

  display: flex;
  justify-content: center;
  gap: ${css.BUTTONS_GAP_SMALL};

  @media (max-width: ${globalConstants.switchTablet}) {
    margin-top: 6px;
    margin-bottom: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const NoResults = styled.div`
  width: 100%;
  margin: 25px 0;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const NoResultsIcon = styled.div`
  width: 32px;
  min-width: 32px;
  height: 32px;
`;

const NoResultsText = styled.div``;

const CountryIcon = styled.div`
  padding-top: 1px;

  width: 18px;
  min-width: 18px;
  height: 18px;

  &:hover {
    cursor: help;
  }
`;

const EllipsisValue = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export default ({ data, limit, page, paginate, onPreviewClick }) => {
  const numOfResults = data.sentResponderEmails.numOfResults;

  const list = data.sentResponderEmails.emails.map((email) => {
    const { id, created_at, subject, html, lead } = email;

    let countryIcon = null;
    let countryName = "";

    if (lead.location_country) {
      const country = COUNTRIES[lead.location_country];

      if (country) {
        countryName = country.name;

        countryIcon = COUNTRY_ICONS[countryName]
          ? COUNTRY_ICONS[countryName].icon
          : null;
      }
    }

    if (!countryIcon) countryIcon = circleGreyIcon;

    return (
      <tr key={id}>
        <Cell width="110px">
          {dateFormatted(new Date(created_at), false, false) +
            ", " +
            getTimeAMPM(new Date(created_at))}
        </Cell>
        <Cell width="160px">
          <CellLineValueWrap>
            <CountryIcon
              data-tooltip-id="countryTip"
              data-tooltip-html={
                lead.location_country ? countryName : "Unknown location"
              }
              data-tooltip-place="top"
              data-tooltip-delay-show={500}
              onClick={(e) => e.stopPropagation()}
            >
              <img src={countryIcon} width="100%" alt={countryName} />
            </CountryIcon>
            <Tooltip id="countryTip" className="tooltip" />
            <EllipsisValue>{lead.name}</EllipsisValue>
          </CellLineValueWrap>
        </Cell>
        <Cell width="160px">
          <EllipsisValue>{lead.email}</EllipsisValue>
        </Cell>
        <Cell width="160px">
          <CellLineValueWrap>
            <ButtonRoundIcon
              icon={mailPreviewIcon}
              hoverIcon={mailPreviewWhiteIcon}
              background={css.BRAND_YELLOW}
              hoverBackground={css.BRAND_GREEN}
              onClick={() => onPreviewClick(id)}
              buttonSize="24px"
              iconSize="12px"
              transform="translateY(1px)"
            />
            <EllipsisValue>{email.subject}</EllipsisValue>
          </CellLineValueWrap>
        </Cell>
        {/* <Cell width="120px">
          <EllipsisValue>{name}</EllipsisValue>
        </Cell>
        <Cell>
          <CellLineValueWrap>
            {email}
            <CopyIconBox>
              <CopyToClipboard text={email} onCopy={() => linkCopied(email)}>
                <ButtonRoundIcon
                  icon={copied === email ? checkIcon : copyIcon}
                  hoverIcon={copied === email ? checkWhiteIcon : copyWhiteIcon}
                  background={css.BRAND_YELLOW}
                  hoverBackground={css.BRAND_GREEN}
                  buttonSize="20px"
                  iconSize="9px"
                />
              </CopyToClipboard>
            </CopyIconBox>
          </CellLineValueWrap>
        </Cell>
        <Cell>
          <BadgeWrap>
            <Badge verified={verified ? 1 : 0}>{verified ? "Yes" : "No"}</Badge>
          </BadgeWrap>
        </Cell>
        <Cell width="130px">
          <CellVeryRight>
            <CellLineValueWrap>
              <EllipsisValue>{page.name}</EllipsisValue>
            </CellLineValueWrap>
          </CellVeryRight>
        </Cell> */}
      </tr>
    );
  });

  // prepare pagination
  let showPagination = false;

  if (numOfResults > limit) showPagination = true;

  const maxPage = Math.ceil(numOfResults / limit);

  return (
    <SentEmailsSection>
      <Header>
        <Title>Sent Emails</Title>
      </Header>
      <LeadList>
        <TableWrap className="table-wrap">
          {list.length > 0 && (
            <ListTable>
              <TableHead>
                <tr>
                  <Label>Date</Label>
                  <Label>Lead</Label>
                  <Label>Email</Label>
                  <Label>Message</Label>
                </tr>
              </TableHead>
              <TableBody>{list}</TableBody>
            </ListTable>
          )}
          {list.length === 0 && (
            <NoResults>
              <NoResultsIcon>
                <img src={blockIcon} style={{ width: "100%" }} />
              </NoResultsIcon>
              <NoResultsText>No emails have been sent yet.</NoResultsText>
            </NoResults>
          )}
        </TableWrap>
        {showPagination && (
          <Pagination>
            {page > 1 && (
              <ButtonRoundIcon
                icon={leftIcon}
                hoverIcon={leftWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                iconSize="16px"
                buttonSize="32px"
                onClick={() => paginate("previous")}
              />
            )}
            {page < maxPage && (
              <ButtonRoundIcon
                icon={rightIcon}
                hoverIcon={rightWhiteIcon}
                background={css.BRAND_YELLOW}
                hoverBackground={css.BRAND_GREEN}
                iconSize="16px"
                buttonSize="32px"
                onClick={() => paginate("next")}
              />
            )}
          </Pagination>
        )}
      </LeadList>
    </SentEmailsSection>
  );
};
